"use strict";
import { BigNumber } from "ethers";
import { useCallback, useMemo } from "react";
import { useAsyncData } from "utilities/src/react/hooks";
var FeeType = /* @__PURE__ */ ((FeeType2) => {
  FeeType2["Legacy"] = "legacy";
  FeeType2["Eip1559"] = "eip1559";
  return FeeType2;
})(FeeType || {});
function weiToUSD(weiValue) {
  const weiAmount = BigNumber.from(weiValue);
  const ethDecimals = 18;
  const divisor = BigNumber.from(10).pow(ethDecimals);
  const wholePart = weiAmount.div(divisor);
  const fractionalPart = weiAmount.mod(divisor);
  const fractionalStr = fractionalPart.toString().padStart(18, "0");
  const significantDigits = fractionalStr.substring(0, 6);
  if (wholePart.isZero() && fractionalPart.isZero()) {
    return "0";
  }
  return wholePart.isZero() ? `0.${significantDigits}` : `${wholePart}.${significantDigits}`;
}
export var GasSpeed = /* @__PURE__ */ ((GasSpeed2) => {
  GasSpeed2["Normal"] = "normal";
  GasSpeed2["Fast"] = "fast";
  GasSpeed2["Urgent"] = "urgent";
  return GasSpeed2;
})(GasSpeed || {});
export function useTransactionGasFee(tx, speed = "urgent" /* Urgent */, skip = !tx) {
  const gasFeeFetcher = useGasFeeQuery(tx, skip);
  const { data, isLoading } = useAsyncData(gasFeeFetcher);
  return useMemo(() => {
    if (!data) {
      return { isLoading };
    }
    const params = data.type === "eip1559" /* Eip1559 */ ? {
      maxPriorityFeePerGas: data.maxPriorityFeePerGas[speed],
      maxFeePerGas: data.maxFeePerGas[speed],
      gasLimit: data.gasLimit
    } : {
      gasPrice: data.gasPrice[speed],
      gasLimit: data.gasLimit
    };
    const gasFeeWei = data.gasFee[speed];
    const gasFeeUSD = weiToUSD(gasFeeWei);
    return {
      value: gasFeeWei,
      valueUSD: gasFeeUSD,
      isLoading,
      params
    };
  }, [data, isLoading, speed]);
}
function createBscGasFeeResponse(tx) {
  const hasData = tx.data && tx.data !== "0x";
  const gasLimitEstimate = hasData ? "300000" : "21000";
  const normalGasPrice = "1000000000";
  const fastGasPrice = "3000000000";
  const urgentGasPrice = "5000000000";
  const normalGasFee = BigNumber.from(gasLimitEstimate).mul(normalGasPrice).toString();
  const fastGasFee = BigNumber.from(gasLimitEstimate).mul(fastGasPrice).toString();
  const urgentGasFee = BigNumber.from(gasLimitEstimate).mul(urgentGasPrice).toString();
  return {
    type: "legacy" /* Legacy */,
    gasLimit: gasLimitEstimate,
    gasFee: {
      normal: normalGasFee,
      fast: fastGasFee,
      urgent: urgentGasFee
    },
    gasPrice: {
      normal: normalGasPrice,
      fast: fastGasPrice,
      urgent: urgentGasPrice
    }
  };
}
function useGasFeeQuery(tx, skip = !tx) {
  const gasFeeFetcher = useCallback(async () => {
    if (skip || !tx) {
      return void 0;
    }
    return createBscGasFeeResponse(tx);
  }, [skip, tx]);
  return gasFeeFetcher;
}
